import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { pl, enGB } from "date-fns/locale";
import { getLang } from "@/i18n";
import { Mail, Plus, Send, CheckCircle2, Trash2, Sparkles, CalendarClock, X, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import DOMPurify from "dompurify";
import { UnsubscribesPanel } from "@/components/UnsubscribesPanel";
import { WelcomeSeriesPanel } from "@/components/WelcomeSeriesPanel";

const SAMPLE_DEFAULT = { firma: "Przykładowa Firma", osoba: "Zespół", kraj: "Polska" };
const mergeSubstitute = (s, v) => (s || "").replaceAll("{{firma}}", v.firma).replaceAll("{{osoba}}", v.osoba).replaceAll("{{kraj}}", v.kraj);
const wrapPreview = (html) => `<div style="font-family:Arial,sans-serif;font-size:15px;line-height:1.6;color:#222">${html}<hr style="margin-top:24px;border:none;border-top:1px solid #eee"/><p style="font-size:12px;color:#888">Otrzymujesz tę wiadomość jako kontakt biznesowy PATH. <a href="#">Wypisz się z tej korespondencji</a>.</p></div>`;

function DateTimePicker({ value, onChange }) {
  const dt = value ? new Date(value) : null;
  const time = dt ? format(dt, "HH:mm") : "09:00";
  const setDate = (d) => {
    if (!d) return;
    const [h, m] = time.split(":");
    d.setHours(parseInt(h || "9", 10), parseInt(m || "0", 10), 0, 0);
    onChange(d.toISOString());
  };
  const setTime = (t) => {
    const base = dt || new Date();
    const [h, m] = t.split(":");
    base.setHours(parseInt(h || "0", 10), parseInt(m || "0", 10), 0, 0);
    onChange(base.toISOString());
  };
  return (
    <div className="flex gap-2 mt-1">
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" data-testid="camp-schedule-btn" className="flex-1 min-w-[150px] justify-start font-normal h-10">
            <CalendarClock className="h-4 w-4 mr-2 shrink-0 text-[#193962]" />
            <span className="truncate">{dt ? format(dt, "d MMM yyyy", { locale: getLang() === "en" ? enGB : pl }) : <span className="text-muted-foreground">Wybierz datę</span>}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={dt || undefined} onSelect={setDate} initialFocus locale={getLang() === "en" ? enGB : pl}
            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} />
        </PopoverContent>
      </Popover>
      <Input type="time" data-testid="camp-schedule-time" value={time} onChange={(e) => setTime(e.target.value)} className="w-24 shrink-0 h-10" disabled={!dt} />
      {dt && <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0" data-testid="camp-schedule-clear" onClick={() => onChange("")}><X className="h-4 w-4" /></Button>}
    </div>
  );
}

const STATUS_LABEL = {
  draft: "Szkic", pending_approval: "Do zatwierdzenia", approved: "Zatwierdzony",
  scheduled: "Zaplanowany", sending: "Wysyłka", sent: "Wysłany",
};

const empty = { title: "", subject: "", body_html: "", scheduled_at: "", tracking: true,
  segment: { region: "all", country: "all", source: "all", status: "all" } };

export default function Campaigns() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [count, setCount] = useState(0);
  const [preview, setPreview] = useState({ open: false, subject: "", html: "", sample: null });

  const load = () => api.get("/campaigns").then((r) => setList(r.data.campaigns)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const s = form.segment;
    api.get(`/campaigns/recipients-count?region=${s.region}&country=${s.country}&source=${s.source}&status=${s.status}`)
      .then((r) => setCount(r.data.count)).catch(() => {});
  }, [form.segment]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSeg = (k, v) => setForm((f) => ({ ...f, segment: { ...f.segment, [k]: v } }));

  const save = async () => {
    if (!form.title || !form.subject || !form.body_html) return toast.error("Uzupełnij tytuł, temat i treść");
    try {
      if (editing) { await api.put(`/campaigns/${editing}`, form); toast.success("Zapisano"); }
      else { await api.post("/campaigns", form); toast.success("Utworzono kampanię"); }
      setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Błąd zapisu"); }
  };
  const edit = (c) => { setEditing(c.campaign_id); setForm({ ...empty, ...c, segment: { ...empty.segment, ...(c.segment || {}) }, scheduled_at: c.scheduled_at || "" }); };
  const act = async (id, path, ok) => { try { await api.post(`/campaigns/${id}/${path}`); toast.success(ok); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Błąd"); } };
  const del = async (id) => { try { await api.delete(`/campaigns/${id}`); toast.success("Usunięto"); load(); } catch { toast.error("Błąd"); } };
  const openPreview = async (subject, body_html, segment) => {
    if (!body_html) return toast.error("Brak treści do podglądu");
    const s = segment || {};
    let v = SAMPLE_DEFAULT;
    try {
      const r = await api.get(`/campaigns/preview-sample?region=${s.region || "all"}&country=${s.country || "all"}&source=${s.source || "all"}&status=${s.status || "all"}`);
      v = r.data;
    } catch { /* fallback to defaults */ }
    const html = DOMPurify.sanitize(wrapPreview(mergeSubstitute(body_html, v)));
    setPreview({ open: true, subject: mergeSubstitute(subject, v) || "(bez tematu)", html, sample: v });
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8" data-testid="campaigns-page">
        <div className="flex items-center gap-2.5 mb-6">
          <Mail className="h-5 w-5 text-[#193962]" />
          <h1 className="font-[Outfit] text-2xl font-semibold tracking-tight">Kampanie mailingowe</h1>
          <span className="text-xs text-muted-foreground font-[Manrope] ml-2">Nadawca: Ben Carson (PATH) · reply-to b.carson@path.com.pl</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="border border-border bg-white rounded-md p-5 space-y-3 min-w-0" data-testid="campaign-editor">
            <h3 className="font-[Outfit] font-semibold flex items-center gap-2"><Plus className="h-4 w-4" /> {editing ? "Edytuj" : "Nowa kampania"}</h3>
            <div><Label className="text-sm">Tytuł (wewn.)</Label><Input data-testid="camp-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-sm">Temat e-maila</Label><Input data-testid="camp-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-sm">Treść (HTML) — merge tagi: {"{{firma}} {{osoba}} {{kraj}}"}</Label>
              <Textarea data-testid="camp-body" rows={8} value={form.body_html} onChange={(e) => set("body_html", e.target.value)} className="mt-1 font-mono text-xs" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-sm">Region</Label>
                <select data-testid="camp-region" value={form.segment.region} onChange={(e) => setSeg("region", e.target.value)} className="mt-1 w-full h-10 border border-border rounded-md px-2 bg-white text-sm">
                  <option value="all">Wszystkie</option><option value="Poland">Poland</option><option value="Kenya">Kenya</option></select></div>
              <div><Label className="text-sm">Zaplanuj na</Label>
                <DateTimePicker value={form.scheduled_at} onChange={(v) => set("scheduled_at", v)} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm font-[Manrope]"><input type="checkbox" data-testid="camp-tracking" checked={form.tracking} onChange={(e) => set("tracking", e.target.checked)} /> Śledzenie otwarć</label>
            <p className="text-xs text-muted-foreground">Odbiorców w segmencie: <strong data-testid="camp-count">{count}</strong></p>
            <div className="flex gap-2">
              <Button onClick={save} data-testid="camp-save" className="bg-[#193962] text-white">{editing ? "Zapisz" : "Utwórz"}</Button>
              <Button variant="outline" onClick={() => openPreview(form.subject, form.body_html, form.segment)} data-testid="camp-preview-btn"><Eye className="h-4 w-4 mr-1" />Podgląd</Button>
              {editing && <Button variant="outline" onClick={() => { setForm(empty); setEditing(null); }}>Anuluj</Button>}
            </div>
          </div>

          <div className="space-y-3 min-w-0" data-testid="campaign-list">
            {list.length === 0 && <p className="text-sm text-muted-foreground font-[Manrope]">Brak kampanii. Utwórz pierwszą lub poczekaj na piątkową propozycję AI.</p>}
            {list.map((c) => (
              <div key={c.campaign_id} className="border border-border bg-white rounded-md p-4" data-testid={`campaign-${c.campaign_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-[Manrope] font-medium text-sm">{c.title}</span>
                  {c.ai_generated && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1"><Sparkles className="h-3 w-3" />AI</span>}
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 ml-auto">{STATUS_LABEL[c.status] || c.status}</span>
                </div>
                <p className="text-xs text-zinc-500 mt-1 truncate" data-no-i18n>{c.subject}</p>
                <p className="text-[11px] text-zinc-400 mt-1">Wysłano {c.stats?.sent || 0} · otwarcia {c.stats?.opens || 0} · wypisy {c.stats?.unsubscribes || 0}{c.scheduled_at ? ` · ⏰ ${new Date(c.scheduled_at).toLocaleString("pl-PL")}` : ""}</p>
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => edit(c)} data-testid={`camp-edit-${c.campaign_id}`}>Edytuj</Button>
                  <Button size="sm" variant="outline" onClick={() => openPreview(c.subject, c.body_html, c.segment)} data-testid={`camp-preview-${c.campaign_id}`}><Eye className="h-3.5 w-3.5 mr-1" />Podgląd</Button>
                  <Button size="sm" variant="outline" onClick={() => act(c.campaign_id, "test-send", "Wysłano test do Ciebie")} data-testid={`camp-test-${c.campaign_id}`}>Test do mnie</Button>
                  {["draft", "pending_approval"].includes(c.status) && <Button size="sm" onClick={() => act(c.campaign_id, "approve", "Zatwierdzono")} data-testid={`camp-approve-${c.campaign_id}`} className="bg-emerald-600 text-white"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Zatwierdź</Button>}
                  {["approved", "scheduled"].includes(c.status) && <Button size="sm" onClick={() => act(c.campaign_id, "send-now", "Wysyłka uruchomiona")} data-testid={`camp-send-${c.campaign_id}`} className="bg-[#193962] text-white"><Send className="h-3.5 w-3.5 mr-1" />Wyślij teraz</Button>}
                  <Button size="sm" variant="ghost" onClick={() => del(c.campaign_id)} data-testid={`camp-del-${c.campaign_id}`} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <WelcomeSeriesPanel />
        <UnsubscribesPanel />
      </main>

      <Dialog open={preview.open} onOpenChange={(o) => setPreview((p) => ({ ...p, open: o }))}>
        <DialogContent className="max-w-2xl" data-testid="campaign-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">Podgląd wiadomości</DialogTitle>
            <DialogDescription className="font-[Manrope]">Tak zobaczy ją klient{preview.sample ? ` (przykład: ${preview.sample.firma})` : ""}. Nadawca: Ben Carson (PATH) · reply-to b.carson@path.com.pl</DialogDescription>
          </DialogHeader>
          <div className="border-b border-border pb-2"><span className="text-xs text-muted-foreground">Temat: </span><span className="font-medium text-sm" data-testid="preview-subject">{preview.subject}</span></div>
          <div className="max-h-[60vh] overflow-auto bg-white border border-border rounded-md p-4" data-testid="preview-body" dangerouslySetInnerHTML={{ __html: preview.html }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
